import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Trash2,
  Calendar,
  X,
  MapPin,
  Clock,
  Users,
  AlertTriangle,
  Plus,
} from 'lucide-react';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';
import isBetween from 'dayjs/plugin/isBetween';
import { useEvents } from '../../hooks/useEvents';
import './eventsList.css';

dayjs.extend(isBetween);

export default function EventsList({
  events = [],
  onDeleteEvent,
  selectedDate = null,
  doctorIdOverride = null,
}) {
  const { t, i18n } = useTranslation('availability');
  const { createDoctorEvent, deleteEventById, loading } = useEvents({
    doctorIdOverride,
    disableDoctorBootstrap: Boolean(doctorIdOverride),
  });

  const currentLang = i18n.language || 'fr';

  const [showAddModal, setShowAddModal] = useState(false);
  const [title, setTitle] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [location, setLocation] = useState('');
  const [submitError, setSubmitError] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const [filter, setFilter] = useState('upcoming');

  const startOfToday = dayjs().startOf('day');

  const upcomingEvents = events
    .filter((event) => !event.toDate.isBefore(startOfToday, 'day'))
    .sort((a, b) => a.fromDate.valueOf() - b.fromDate.valueOf());

  const pastEvents = events
    .filter((event) => event.toDate.isBefore(startOfToday, 'day'))
    .sort((a, b) => b.fromDate.valueOf() - a.fromDate.valueOf());

  const visibleEvents = filter === 'upcoming' ? upcomingEvents : pastEvents;

  const openAddModal = () => {
    setTitle('');
    setFromDate(selectedDate ? selectedDate.format('YYYY-MM-DD') : '');
    setToDate(selectedDate ? selectedDate.format('YYYY-MM-DD') : '');
    setStartTime('09:00');
    setEndTime('10:00');
    setLocation('');
    setSubmitError(null);
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title || !fromDate || !toDate) return;

    setSubmitError(null);

    try {
      await createDoctorEvent({
        name: title,
        fromDate,
        toDate,
        startTime: startTime.substring(0, 5),
        endTime: endTime.substring(0, 5),
        location: location || undefined,
      });

      setShowAddModal(false);
    } catch (err) {
      setSubmitError(err?.message ?? t('events.errors.create'));
    }
  };

  const handleDeleteClick = (event) => {
    setDeleteError(null);
    setConfirmDelete({
      id: event.id,
      title: event.title,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;

    setDeleteLoading(true);
    setDeleteError(null);

    try {
      await deleteEventById(confirmDelete.id);
      onDeleteEvent?.(confirmDelete.id);
      setConfirmDelete(null);
    } catch (err) {
      setDeleteError(err?.message ?? t('events.errors.delete'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const hasScrollableList = visibleEvents.length > 2;

  return (
    <>
      <div className="events-card">
        <div className="events-header">
          <div className="events-header__info">
            <p className="events-header__title">{t('events.title')}</p>
            <p className="events-header__subtitle">{t('events.subtitle')}</p>
          </div>

          <span className="events-badge">
            {t('events.count', { count: visibleEvents.length })}
          </span>
        </div>

        <div className="events-tabs">
          <button
            type="button"
            className={`events-tab${filter === 'upcoming' ? ' events-tab--active' : ''}`}
            onClick={() => setFilter('upcoming')}
          >
            {t('events.filters.upcoming')}
            {upcomingEvents.length > 0 && (
              <span className="events-tab__count">{upcomingEvents.length}</span>
            )}
          </button>

          <button
            type="button"
            className={`events-tab${filter === 'history' ? ' events-tab--active' : ''}`}
            onClick={() => setFilter('history')}
          >
            {t('events.filters.history')}
            {pastEvents.length > 0 && (
              <span className="events-tab__count">{pastEvents.length}</span>
            )}
          </button>
        </div>

        <div
          className={`events-list-wrap${
            hasScrollableList ? ' events-list-wrap--scrollable' : ''
          }`}
        >
          <ul className="events-list">
            {visibleEvents.length === 0 ? (
              <li className="events-empty">
                <Calendar size={28} />
                <p>
                  {filter === 'upcoming'
                    ? t('events.empty')
                    : t('events.emptyHistory')}
                </p>
                <p className="events-empty__sub">{t('events.emptyHint')}</p>
              </li>
            ) : (
              visibleEvents.map((event) => (
                <EventRow
                  key={event.id}
                  event={event}
                  onDelete={() => handleDeleteClick(event)}
                  t={t}
                  currentLang={currentLang}
                />
              ))
            )}
          </ul>
        </div>

        <div className="events-footer">
          <button
            className="events-add-btn"
            onClick={openAddModal}
            type="button"
          >
            <Plus size={15} />
            {t('events.add')}
          </button>
        </div>
      </div>

      {showAddModal && (
        <div
          className="event-modal-overlay"
          onClick={() => setShowAddModal(false)}
        >
          <div className="event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="event-modal__header">
              <div className="event-modal__header-left">
                <div className="event-modal__icon-wrap">
                  <Calendar size={20} className="event-modal__icon" />
                </div>

                <div>
                  <h2 className="event-modal__title">
                    {t('events.form.addTitle')}
                  </h2>
                  <p className="event-modal__subtitle">
                    {t('events.form.addSubtitle')}
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="event-modal__close"
                onClick={() => setShowAddModal(false)}
              >
                <X size={22} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="event-modal__body">
              <div className="event-modal__field">
                <label className="event-modal__label">
                  {t('events.form.title')}
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('events.form.titlePlaceholder')}
                  required
                  className="event-modal__input"
                />
              </div>

              <div className="event-modal__date-grid">
                <div className="event-modal__field">
                  <label className="event-modal__label">
                    {t('events.form.fromDate')}
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    required
                    className="event-modal__input"
                  />
                </div>

                <div className="event-modal__field">
                  <label className="event-modal__label">
                    {t('events.form.toDate')}
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    required
                    min={fromDate}
                    className="event-modal__input"
                  />
                </div>
              </div>

              <div className="event-modal__date-grid">
                <div className="event-modal__field">
                  <label className="event-modal__label">
                    {t('events.form.startTime')}
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required
                    className="event-modal__input"
                  />
                </div>

                <div className="event-modal__field">
                  <label className="event-modal__label">
                    {t('events.form.endTime')}
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    required
                    className="event-modal__input"
                  />
                </div>
              </div>

              <div className="event-modal__field">
                <label className="event-modal__label">
                  {t('events.form.location')}
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder={t('events.form.locationPlaceholder')}
                  className="event-modal__input"
                />
              </div>

              {submitError && (
                <div className="event-modal__notice event-modal__notice--error">
                  <p>{submitError}</p>
                </div>
              )}

              <div className="event-modal__notice">
                <p>{t('events.form.notice')}</p>
              </div>

              <div className="event-modal__footer">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="event-modal__btn event-modal__btn--cancel"
                  disabled={loading}
                >
                  {t('events.form.cancel')}
                </button>

                <button
                  type="submit"
                  className="event-modal__btn event-modal__btn--confirm"
                  disabled={loading}
                >
                  {loading ? t('events.form.saving') : t('events.form.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div
          className="event-modal-overlay"
          onClick={() => !deleteLoading && setConfirmDelete(null)}
        >
          <div
            className="event-modal event-modal--confirm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="event-modal__header">
              <div className="event-modal__header-left">
                <div className="event-modal__icon-wrap event-modal__icon-wrap--danger">
                  <AlertTriangle
                    size={20}
                    className="event-modal__icon event-modal__icon--danger"
                  />
                </div>

                <div>
                  <h2 className="event-modal__title">
                    {t('events.delete.title')}
                  </h2>
                  <p className="event-modal__subtitle">
                    {t('events.delete.subtitle')}
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="event-modal__close"
                onClick={() => setConfirmDelete(null)}
                disabled={deleteLoading}
              >
                <X size={22} />
              </button>
            </div>

            <div className="event-modal__body">
              <div className="event-delete-confirm__body">
                <p className="event-delete-confirm__message">
                  {t('events.delete.message')}{' '}
                  <strong>« {confirmDelete.title} »</strong> ?
                </p>

                <p className="event-delete-confirm__sub">
                  {t('events.delete.description')}
                </p>
              </div>

              {deleteError && (
                <div className="event-modal__notice event-modal__notice--error">
                  <p>{deleteError}</p>
                </div>
              )}

              <div className="event-modal__footer">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(null)}
                  className="event-modal__btn event-modal__btn--cancel"
                  disabled={deleteLoading}
                >
                  {t('events.delete.cancel')}
                </button>

                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="event-modal__btn event-modal__btn--danger"
                  disabled={deleteLoading}
                >
                  {deleteLoading
                    ? t('events.delete.deleting')
                    : t('events.delete.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function EventRow({ event, onDelete, t, currentLang }) {
  const sameDay = event.fromDate.isSame(event.toDate, 'day');

  const dayLabel = sameDay
    ? event.fromDate.format('D')
    : `${event.fromDate.format('D')}–${event.toDate.format('D')}`;

  return (
    <li className="event-row">
      <div className="event-row__date-block">
        <span className="event-row__day">{dayLabel}</span>
        <span className="event-row__month">
          {event.fromDate.locale(currentLang).format('MMM')}
        </span>
        <span className="event-row__year">
          {event.fromDate.format('YYYY')}
        </span>
      </div>

      <div className="event-row__content">
        <p className="event-row__title">{event.title}</p>

        {event.location && (
          <p className="event-row__meta">
            <MapPin size={11} />
            {event.location}
          </p>
        )}
        <p className="event-row__meta">
          <Clock size={11} />
          {String(event.startTime).padStart(2, '0')}:00 –{' '}
          {String(event.endTime).padStart(2, '0')}:00
        </p>

        {!!event.participants?.length && (
          <p className="event-row__meta">
            <Users size={11} />
            {t('events.participants', {
              count: event.participants.length,
            })}
          </p>
        )}
      </div>

      {onDelete && (
        <button
          className="event-row__delete"
          onClick={onDelete}
          title={t('events.delete.tooltip')}
          type="button"
        >
          <Trash2 size={13} />
        </button>
      )}
    </li>
  );
}